<template>
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4 flex justify-between items-center">
      <div>
        <div class="flex items-center gap-2 mb-1">
          <button @click="goBack" class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white">
            {{ isEdit ? 'Editar Cotización' : 'Nueva Cotización' }}
          </h1>
          <span v-if="cotizacionForm.numero_cotizacion" class="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
            {{ cotizacionForm.numero_cotizacion }}
          </span>
        </div>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ isEdit ? 'Modifica los datos de la cotización' : 'Crea una nueva cotización agregando prototipos' }}
        </p>
      </div>
      <div class="flex gap-2">
        <button @click="goBack" class="px-3 py-1.5 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm">
          Descartar
        </button>
        <button v-if="isEdit && cotizacionId" @click="downloadPDF" class="px-3 py-1.5 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          PDF
        </button>
        <button @click="saveDraft" :disabled="loading" class="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm">
          {{ loading ? 'Guardando...' : 'Guardar Borrador' }}
        </button>
        <button @click="saveCotizacion" :disabled="loading || items.length === 0" class="px-3 py-1.5 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm">
          {{ isEdit ? 'Actualizar' : 'Crear' }} Cotización
        </button>
      </div>
    </div>

    <!-- Estado de auto-guardado -->
    <div v-if="autoSaveStatus" class="mb-4">
      <div :class="autoSaveStatusClass" class="px-3 py-1 rounded-md text-xs font-medium">
        {{ autoSaveStatus }}
      </div>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <!-- Información de la Cotización -->
      <div class="xl:col-span-2 space-y-4">
        <!-- Datos básicos -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3">Información de la Cotización</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            

            <div>
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha de vencimiento</label>
              <input
                v-model="cotizacionForm.fecha_vencimiento"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>

          <div class="mt-4">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descripción</label>
            <textarea 
              v-model="cotizacionForm.descripcion" 
              rows="3" 
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm resize-none" 
              placeholder="Descripción adicional de la cotización"
            ></textarea>
          </div>
        </div>

        <!-- Datos del cliente -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex justify-between items-center mb-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Información del Cliente</h2>
            <button
              @click="openClientSelector"
              class="px-3 py-1.5 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors text-sm flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              {{ selectedClient ? 'Cambiar Cliente' : 'Seleccionar Cliente' }}
            </button>
          </div>
          
          <div v-if="selectedClient" class="space-y-3">
            <!-- Información del cliente seleccionado -->
            <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nombre completo</label>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">{{ selectedClient.nombre_completo }}</p>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  <p class="text-sm text-gray-700 dark:text-gray-300">{{ selectedClient.email }}</p>
                </div>
                <div>
                  <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Teléfono</label>
                  <p class="text-sm text-gray-700 dark:text-gray-300">{{ selectedClient.telefono }}</p>
                </div>
                <div v-if="selectedClient.direccion_completa">
                  <label class="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Dirección</label>
                  <p class="text-sm text-gray-700 dark:text-gray-300">{{ selectedClient.direccion_completa }}</p>
                </div>
              </div>
            </div>

            <!-- Descuento -->
            <!-- <div class="max-w-xs">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Descuento ($)</label>
              <input 
                v-model="cotizacionForm.descuento" 
                type="number" 
                step="0.01"
                min="0"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm" 
                placeholder="0.00"
              />
            </div> -->
          </div>

          <div v-else class="text-center py-8">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
            </svg>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">No hay cliente seleccionado</p>
            <p class="text-xs text-gray-400 dark:text-gray-500">Haz clic en "Seleccionar Cliente" para elegir un cliente</p>
          </div>
        </div>

        <!-- Items de la cotización -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white">Productos Cotizados</h2>
            <button @click="openSearchModal" class="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Agregar Prototipo
            </button>
          </div>

          <!-- Lista de items -->
          <div v-if="items.length > 0" class="space-y-3">
            <div v-for="(item, index) in items" :key="item.temp_id || item.id" class="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
              <div class="flex justify-between items-start mb-2">
                <div class="flex-1">
                  <div class="flex items-center gap-3 mb-1">
                    <h4 class="text-sm font-medium text-gray-900 dark:text-white">{{ item.prototipo?.folio }}</h4>
                    <span class="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                      💵 Costo Unit. (x1): ${{ Number(item.costoUnitario || 0).toFixed(2) }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    👤 {{ item.prototipo?.cliente }} | 📁 {{ item.prototipo?.proyecto }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.prototipo?.descripcion || 'Sin descripción' }}</p>
                  <div class="text-xs text-gray-400 mt-1" v-if="item.prototipo?.largo">
                    📐 {{ item.prototipo?.largo }}×{{ item.prototipo?.ancho }}×{{ item.prototipo?.alto }} cm | Poses: {{ item.prototipo?.poses || 1 }}
                  </div>
                </div>
                <button @click="removeItem(index)" class="text-red-500 hover:text-red-700 p-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                </button>
              </div>

              <div class="space-y-3">
                <!-- Cantidad -->
                <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cantidad</label>
                    <input
                      v-model.number="item.cantidad"
                      @input="recalcularPreciosItem(item)"
                      type="number"
                      min="1"
                      class="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div v-if="item.totalHerramentales > 0" class="text-right col-span-3">
                    <div class="text-xs text-blue-600 dark:text-blue-400">Herramentales (único)</div>
                    <div class="text-sm font-medium text-blue-600 dark:text-blue-400">
                      ${{ Number(item.totalHerramentales || 0).toFixed(2) }}
                    </div>
                  </div>
                </div>

                <!-- Opciones de precio (tarjetas seleccionables) -->
                <!-- <div>
                  <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Selecciona precio base:</label>
                  <div class="grid grid-cols-4 gap-2">
                    <button
                      v-for="opcion in 4"
                      :key="opcion"
                      @click="cambiarOpcionPrecio(item, opcion)"
                      type="button"
                      class="p-2 border-2 rounded-lg transition-all hover:border-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 text-center"
                      :class="item.opcionPrecioSeleccionada === opcion
                        ? 'border-violet-600 bg-violet-100 dark:bg-violet-900/40 dark:border-violet-500'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'"
                    >
                      <div class="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">
                        Opción {{ opcion }}
                      </div>
                      <div class="text-xs text-violet-600 dark:text-violet-400 font-medium mt-0.5">
                        +{{ Number(item[`porcentaje${opcion}`] || 0).toFixed(0) }}%
                      </div>
                      <div class="text-sm font-bold text-gray-900 dark:text-white mt-1">
                        ${{ Number(item[`precioVentaOpcion${opcion}`] || 0).toFixed(2) }}
                      </div>
                    </button>
                  </div>
                </div> -->

                <!-- Precio final editable y subtotal -->
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      💰 Precio Final Unitario
                      <span v-if="item.precioManualmenteEditado" class="text-amber-500" title="Precio editado manualmente">✏️</span>
                    </label>
                    <input
                      v-model.number="item.precioVentaFinal"
                      @input="item.precioManualmenteEditado = true; item.precioVentaFinal = parseFloat(Number(item.precioVentaFinal).toFixed(4)); item.precio_unitario = parseFloat(Number(item.precioVentaFinal).toFixed(4))"
                      type="number"
                      step="0.0001"
                      min="0"
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                      :class="{ 'border-amber-400 ring-2 ring-amber-200': item.precioManualmenteEditado }"
                    />
                  </div>

                  <div class="text-right self-end">
                    <div class="text-xs text-gray-600 dark:text-gray-400 mb-1">Subtotal Producto</div>
                    <div class="text-xl font-bold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">
                      ${{ ((item.cantidad || 1) * (item.precioVentaFinal || 0)).toFixed(4) }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-2">
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notas del item</label>
                <input
                  v-model="item.notas"
                  type="text"
                  class="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs focus:outline-none focus:ring-1 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Notas adicionales para este item..."
                />
              </div>

              <!-- DEBUG: Datos de cálculo -->
              <!-- <details class="mt-3 text-xs border-t border-gray-200 dark:border-gray-600 pt-2">
                <summary class="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-violet-600 font-medium">
                  🔍 Debug - Datos de cálculo
                </summary>
                <div class="mt-2 space-y-1 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                  <div class="grid grid-cols-2 gap-2">
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Cantidad Prototipo:</span> {{ item.prototipo?.cantidad || 'N/A' }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Poses:</span> {{ item.prototipo?.poses || 1 }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Costo Unitario:</span> ${{ Number(item.costoUnitario || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Suma Costos Ajuste:</span> ${{ Number(item.sumaCostosAjuste || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Costo Ajuste Recalc:</span> ${{ Number(item.costoAjusteRecalculado || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Costo Total Unit:</span> ${{ Number(item.costoTotalUnitario || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Costo Herramentales:</span> ${{ Number(item.costoHerramentales || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-gray-700 dark:text-gray-300">Total Herramentales:</span> ${{ Number(item.totalHerramentales || 0).toFixed(4) }}</div>
                  </div>
                  <div class="border-t border-gray-300 dark:border-gray-600 my-2"></div>
                  <div class="font-semibold text-gray-700 dark:text-gray-300 mb-1">Precios Calculados:</div>
                  <div class="grid grid-cols-2 gap-2">
                    <div>Opción 1 ({{ item.porcentaje1 }}%): ${{ Number(item.precioVentaOpcion1 || 0).toFixed(4) }}</div>
                    <div>Opción 2 ({{ item.porcentaje2 }}%): ${{ Number(item.precioVentaOpcion2 || 0).toFixed(4) }}</div>
                    <div>Opción 3 ({{ item.porcentaje3 }}%): ${{ Number(item.precioVentaOpcion3 || 0).toFixed(4) }}</div>
                    <div>Opción 4 ({{ item.porcentaje4 }}%): ${{ Number(item.precioVentaOpcion4 || 0).toFixed(4) }}</div>
                  </div>
                  <div class="border-t border-gray-300 dark:border-gray-600 my-2"></div>
                  <div class="grid grid-cols-2 gap-2">
                    <div><span class="font-semibold text-violet-700 dark:text-violet-300">Opción Seleccionada:</span> {{ item.opcionPrecioSeleccionada }}</div>
                    <div><span class="font-semibold text-violet-700 dark:text-violet-300">Precio Final:</span> ${{ Number(item.precioVentaFinal || 0).toFixed(4) }}</div>
                    <div><span class="font-semibold text-amber-600 dark:text-amber-400">Editado Manual:</span> {{ item.precioManualmenteEditado ? 'Sí ✏️' : 'No' }}</div>
                    <div><span class="font-semibold text-green-700 dark:text-green-300">Precio Unitario:</span> ${{ Number(item.precio_unitario || 0).toFixed(4) }}</div>
                  </div>
                  <div class="border-t border-gray-300 dark:border-gray-600 my-2"></div>
                  <div class="font-semibold text-gray-700 dark:text-gray-300 mb-1">Datos del Prototipo (Base):</div>
                  <div class="grid grid-cols-2 gap-2">
                    <div>Suma Costos Afectados Por Poses: ${{ Number(item.prototipo?.sumaCostosAfectadosPorPoses || 0).toFixed(4) }}</div>
                    <div>Suma Costos Adicionales: ${{ Number(item.prototipo?.sumaCostosAdicionales || 0).toFixed(4) }}</div>
                    <div>Costo Ajuste (original): ${{ Number(item.prototipo?.costoAjuste || 0).toFixed(4) }}</div>
                    <div>Costo Unitario (original): ${{ Number(item.prototipo?.costoUnitario || 0).toFixed(4) }}</div>
                  </div>
                </div>
              </details> -->
            </div>
          </div>

          <!-- Estado vacío -->
          <div v-else class="text-center py-8 text-gray-400">
            <svg class="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
            </svg>
            <p class="text-sm">No hay productos agregados</p>
            <p class="text-xs">Haz clic en "Agregar Prototipo" para comenzar</p>
          </div>
        </div>
      </div>

      <!-- Panel lateral -->
      <div class="space-y-4">
        <!-- Resumen -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-4">Resumen</h2>
          
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Items:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ items.length }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Cantidad total:</span>
              <span class="font-medium text-gray-900 dark:text-white">{{ totalCantidad }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span class="font-medium text-gray-900 dark:text-white">${{ subtotal.toFixed(2) }}</span>
            </div>
            <div v-if="herramentalTotal > 0" class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Herramental:</span>
              <span class="font-medium text-orange-600 dark:text-orange-400">${{ herramentalTotal.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">IVA (16%):</span>
              <span class="font-medium text-gray-900 dark:text-white">${{ iva.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600 dark:text-gray-400">Descuento:</span>
              <span class="font-medium text-red-600 dark:text-red-400">-${{ (cotizacionForm.descuento || 0) }}</span>
            </div>
            <div class="border-t border-gray-200 dark:border-gray-600 pt-2 flex justify-between text-base">
              <span class="font-semibold text-gray-900 dark:text-white">Total:</span>
              <span class="font-bold text-green-600 dark:text-green-400">${{ total.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Notas adicionales -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h2 class="text-base font-semibold text-gray-900 dark:text-white mb-3">Notas Adicionales</h2>
          <textarea 
            v-model="cotizacionForm.notas" 
            rows="4" 
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm resize-none" 
            placeholder="Notas internas de la cotización..."
          ></textarea>
        </div>
      </div>
    </div>

    <!-- Modal de búsqueda de prototipos -->
    <div v-if="showSearchModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] flex flex-col">
        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div class="flex justify-between items-center">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Agregar Prototipos</h3>
            <button @click="showSearchModal = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div class="relative">
            <input 
              v-model="searchTerm"
              @input="debouncedSearchPrototipos"
              type="text" 
              placeholder="Buscar prototipos por nombre o descripción..." 
              class="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
            />
            <svg class="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4">
          <div v-if="searchLoading" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-2">Buscando prototipos...</p>
          </div>

          <div v-else-if="searchResults.length === 0 && searchTerm" class="text-center py-8 text-gray-400">
            <p class="text-sm">No se encontraron prototipos</p>
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            <div
              v-for="prototipo in searchResults"
              :key="prototipo.prototipoId"
              class="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition group"
            >
              <!-- Header del prototipo -->
              <div class="mb-3">
                <h4 class="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1" :title="prototipo.folio">
                  {{ prototipo.folio }}
                </h4>
                <p class="text-xs text-gray-500 dark:text-gray-400 truncate mb-1">
                  👤 {{ prototipo.cliente }} | 📁 {{ prototipo.proyecto }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-2" :title="prototipo.descripcion">
                  {{ prototipo.descripcion || 'Sin descripción' }}
                </p>
                <div class="text-xs text-gray-400 mb-2" v-if="prototipo.largo && prototipo.ancho && prototipo.alto">
                  📐 {{ prototipo.largo }}×{{ prototipo.ancho }}×{{ prototipo.alto }} cm
                </div>
              </div>

              <!-- Información de costo unitario -->
              <div class="mb-3">
                <div class="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">💰 Costo Unitario:</div>
                <div class="bg-gray-100 dark:bg-gray-700 rounded px-2 py-1 text-center">
                  <div class="font-medium text-green-600 dark:text-green-400">
                    ${{ prototipo.costoUnitario ? Number(prototipo.costoUnitario).toFixed(2) : '0.00' }}
                  </div>
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                  Poses: {{ prototipo.poses || 1 }}
                </div>
              </div>

              <!-- Botón de agregar -->
              <button
                @click="addPrototipoToItems(prototipo)"
                class="w-full py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors text-sm font-medium flex items-center justify-center gap-2 group-hover:bg-violet-700"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
                Agregar a Cotización
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Selector de Clientes -->
    <div v-if="showClientSelector" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">Seleccionar Cliente</h3>
          <button @click="closeClientSelector" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Buscador -->
        <div class="mb-4">
          <div class="relative">
            <input
              v-model="clientSearchTerm"
              @input="debouncedSearchClients"
              @keyup.enter="searchClients"
              type="text"
              placeholder="Buscar cliente por nombre, email o teléfono..."
              class="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            />
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>
            <button
              @click="searchClients"
              class="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <svg class="w-5 h-5 text-violet-500 hover:text-violet-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Lista de clientes -->
        <div v-if="loadingClients" class="text-center py-8">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
          <p class="mt-2 text-gray-500 dark:text-gray-400">Buscando clientes...</p>
        </div>

        <div v-else-if="availableClients.length > 0" class="space-y-2 max-h-96 overflow-y-auto">
          <div
            v-for="cliente in availableClients"
            :key="cliente.id"
            @click="selectClient(cliente)"
            class="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            :class="selectedClient?.id === cliente.id ? 'ring-2 ring-violet-500 bg-violet-50 dark:bg-violet-900/20' : ''"
          >
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h4 class="font-medium text-gray-900 dark:text-white">{{ cliente.nombre_completo }}</h4>
                <div class="flex flex-wrap gap-4 mt-1">
                  <span class="text-sm text-gray-600 dark:text-gray-400">{{ cliente.email }}</span>
                  <span class="text-sm text-gray-600 dark:text-gray-400">{{ cliente.telefono }}</span>
                </div>
                <div v-if="cliente.direccion_completa" class="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  {{ cliente.direccion_completa }}
                </div>
              </div>
              <span 
                :class="cliente.estatus === 'activo' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'"
                class="px-2 py-1 text-xs font-medium rounded-full"
              >
                {{ cliente.estatus === 'activo' ? 'Activo' : 'Inactivo' }}
              </span>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-8">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
          </svg>
          <p class="mt-2 text-gray-500 dark:text-gray-400">No se encontraron clientes</p>
          <p class="text-sm text-gray-400 dark:text-gray-500">
            {{ clientSearchTerm ? 'Intenta con otros términos de búsqueda' : 'No hay clientes registrados en el sistema' }}
          </p>
        </div>

        <!-- Botones del modal -->
        <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            @click="clearClientSelection"
            class="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
          >
            Quitar Cliente
          </button>
          <button
            @click="closeClientSelector"
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Borradores Existentes -->
  <div v-if="showDraftsModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
      <div class="flex justify-between items-center mb-4">
        <div>
          <h3 class="text-xl font-bold text-gray-900 dark:text-white">Tienes borradores sin terminar</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">¿Quieres continuar con alguno de estos borradores o empezar una nueva cotización?</p>
        </div>
        <button @click="startNewQuotation" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loadingDrafts" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
        <p class="mt-2 text-gray-500 dark:text-gray-400">Cargando borradores...</p>
      </div>

      <!-- Lista de borradores -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div
          v-for="draft in availableDrafts"
          :key="draft.id"
          @click="continueWithDraft(draft)"
          class="border border-gray-200 dark:border-gray-600 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
        >
          <div class="flex justify-between items-start mb-2">
            <h4 class="font-medium text-gray-900 dark:text-white">{{ draft.nombre }}</h4>
            <span class="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 text-xs rounded-full">
              Borrador
            </span>
          </div>

          <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {{ draft.descripcion || 'Sin descripción' }}
          </p>

          <div class="text-sm text-gray-500 dark:text-gray-500 mb-2">
            <span v-if="draft.cliente">Cliente: {{ draft.cliente.nombre_completo }}</span>
            <span v-else>Sin cliente asignado</span>
          </div>

          <div class="flex justify-between items-center text-sm">
            <span class="text-gray-500 dark:text-gray-400">
              {{ draft.items_count || 0 }} items
            </span>
            <span class="text-gray-400 dark:text-gray-500">
              {{ formatDraftDate(draft.updated_at) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
        <button
          @click="startNewQuotation"
          class="px-6 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Empezar Nueva Cotización
        </button>
        <router-link
          to="/cotizaciones"
          class="px-6 py-2 text-sm font-medium text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-300"
        >
          Ver Todas las Cotizaciones
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import { useUserStore } from '@/stores/user'

const router = useRouter()
const route = useRoute()
const { get, post, put, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()
const userStore = useUserStore()

// Estado
const isEdit = computed(() => !!route.params.id)
const cotizacionId = computed(() => route.params.id)

// Modal de múltiples borradores
const showDraftsModal = ref(false)
const availableDrafts = ref([])
const loadingDrafts = ref(false)

// Formulario principal
const cotizacionForm = ref({
  descripcion: '',
  cliente_id: null,
  fecha_vencimiento: '',
  descuento: 0,
  notas: '',
  numero_cotizacion: '',
})

// Items de la cotización
const items = ref([])
const validationErrors = ref({})

// Búsqueda de prototipos
const showSearchModal = ref(false)
const searchTerm = ref('')
const searchResults = ref([])
const searchLoading = ref(false)

// Selector de clientes
const showClientSelector = ref(false)
const selectedClient = ref(null)
const availableClients = ref([])
const clientSearchTerm = ref('')
const loadingClients = ref(false)

// Auto-guardado
const autoSaveStatus = ref('')
const autoSaveStatusClass = ref('')
const autoSaveTimer = ref(null)
const lastSavedAt = ref(null)
const autoSaveEnabled = ref(false) // Bandera para controlar auto-guardado (por defecto deshabilitado)

// Computados
const totalCantidad = computed(() => {
  return items.value.reduce((sum, item) => sum + (item.cantidad || 0), 0)
})

const subtotal = computed(() => {
  return items.value.reduce((sum, item) => {
    return sum + ((item.cantidad || 1) * (item.precioVentaFinal || item.precio_unitario || 0))
  }, 0)
})

// Calcular herramental total (se cobra una sola vez por item, no se multiplica por cantidad)
const herramentalTotal = computed(() => {
  return items.value.reduce((sum, item) => {
    return sum + parseFloat(item.totalHerramentales || 0)
  }, 0)
})

const iva = computed(() => {
  return (subtotal.value + herramentalTotal.value) * 0.16
})

const total = computed(() => {
  return subtotal.value + herramentalTotal.value + iva.value - (cotizacionForm.value.descuento || 0)
})

// Cargar datos iniciales
const loadCotizacionData = async () => {
  console.log('loadCotizacionData called, isEdit:', isEdit.value, 'cotizacionId:', cotizacionId.value)

  if (!isEdit.value) return

  try {
    console.log('Llamando GET a:', `cotizaciones/${cotizacionId.value}`)
    await get(`cotizaciones/${cotizacionId.value}`)
    console.log('Response status:', data.value)
    if (data.value) {
      console.log('Datos recibidos del backend:', data.value)
      console.log('Datos completos (JSON):', JSON.stringify(data.value, null, 2))
      cotizacionForm.value = {
        descripcion: data.value.descripcion || '',
        cliente_id: data.value.cliente_id || null,
        fecha_vencimiento: data.value.fecha_vencimiento || '',
        descuento: data.value.descuento || 0,
        notas: data.value.notas || '',
        numero_cotizacion: data.value.numero_cotizacion || ''
      }

      // Cargar información del cliente si existe
      if (data.value.cliente) {
        selectedClient.value = data.value.cliente
      }

      // Cargar items
      if (data.value.items) {
        console.log('=== DEBUG: Cargando items existentes ===')
        console.log('Items del backend:', data.value.items)

        items.value = data.value.items.map((item, index) => {
          return {
            id: item.id,
            prototipo_id: item.prototipo_id,
            prototipo: item.prototipo,
            cantidad: item.cantidad,

            // Costos
            costoUnitario: parseFloat(item.costoUnitario || 0),
            sumaCostosAjuste: parseFloat(item.sumaCostosAjuste || 0),
            costoHerramentales: parseFloat(item.costoHerramentales || 0),
            costoAjusteRecalculado: parseFloat(item.costoAjusteRecalculado || 0),
            costoTotalUnitario: parseFloat(item.costoTotalUnitario || 0),

            // Los 4 precios calculados
            precioVentaOpcion1: parseFloat(item.precioVentaOpcion1 || 0),
            precioVentaOpcion2: parseFloat(item.precioVentaOpcion2 || 0),
            precioVentaOpcion3: parseFloat(item.precioVentaOpcion3 || 0),
            precioVentaOpcion4: parseFloat(item.precioVentaOpcion4 || 0),

            // Porcentajes
            porcentaje1: parseFloat(item.porcentaje1 || 0),
            porcentaje2: parseFloat(item.porcentaje2 || 0),
            porcentaje3: parseFloat(item.porcentaje3 || 0),
            porcentaje4: parseFloat(item.porcentaje4 || 0),

            // Selección y precio final
            opcionPrecioSeleccionada: item.opcionPrecioSeleccionada || 1,
            precioVentaFinal: parseFloat(item.precioVentaFinal || 0),
            precioManualmenteEditado: item.precioManualmenteEditado || false,

            // Herramentales
            totalHerramentales: parseFloat(item.totalHerramentales || 0),

            // Compatibilidad legacy
            precio_unitario: parseFloat(item.precio_unitario || item.precioVentaFinal || 0),
            tipo_precio: item.tipo_precio || 'precio1',
            notas: item.notas || '',
            temp_id: `existing-${item.id}`
          }
        })

        console.log('Items cargados:', items.value)
      }
    }
  } catch (err) {
    console.error('Error en loadCotizacionData:', err)
    console.error('Error details:', err.response || err.message)
    showError('Error al cargar datos de la cotización')
  }
}

// Búsqueda de prototipos
const searchPrototipos = async (loadInitial = false) => {
  searchLoading.value = true
  try {
    const params = new URLSearchParams()
    if (searchTerm.value.trim()) {
      params.append('search', searchTerm.value)
    }

    console.log('Searching prototipos with params:', params.toString())
    await get(`cotizaciones-search-prototipos-new?${params.toString()}`)
    if (data.value) {
      console.log('Prototipos found:', data.value)
      searchResults.value = data.value
    } else {
      console.log('No prototipos data received')
      searchResults.value = []
    }
  } catch (err) {
    showError('Error al buscar prototipos')
    searchResults.value = []
  } finally {
    searchLoading.value = false
  }
}

// Búsqueda con debounce
const debouncedSearchPrototipos = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(searchPrototipos, 300)
  }
})()

// Cargar prototipos iniciales cuando se abre el modal
const loadInitialPrototipos = async () => {
  // Cargar algunos prototipos iniciales sin filtro
  await searchPrototipos(true)
}

// Agregar prototipo a items
const addPrototipoToItems = async (prototipo) => {
  // Verificar si ya existe
  const existingIndex = items.value.findIndex(item => item.prototipo_id === prototipo.prototipoId)

  if (existingIndex >= 0) {
    // Si ya existe, incrementar cantidad y recalcular
    items.value[existingIndex].cantidad++
    await recalcularPreciosItem(items.value[existingIndex])
    showSuccess(`Cantidad de "${prototipo.folio}" incrementada`)
  } else {
    // Calcular precios iniciales con cantidad = 1
    const negocio = userStore.negocioSeleccionado
    if (!negocio) {
      showError('Selecciona un negocio primero')
      return
    }

    try {
      // Llamar al endpoint para calcular precios
      await post('cotizaciones-calcular-precios', {
        prototipo_id: prototipo.prototipoId,
        cantidad: 1,
        negocio_id: negocio.id
      })

      const precios = data.value

      // Agregar nuevo item con todos los datos calculados
      items.value.push({
        temp_id: `temp-${Date.now()}-${Math.random()}`,
        prototipo_id: prototipo.prototipoId,
        prototipo: prototipo,
        cantidad: 1,
        opcionPrecioSeleccionada: 1, // Por defecto opción 1
        precioManualmenteEditado: false,
        // Datos calculados
        ...precios,
        // Precio final (inicialmente opción 1)
        precioVentaFinal: 0.0,//parseFloat(Number(precios.precioVentaOpcion1).toFixed(4)),
        precio_unitario: 0.0,//parseFloat(Number(precios.precioVentaOpcion1).toFixed(4)), // Para compatibilidad
        notas: ''
      })

      showSuccess(`"${prototipo.folio}" agregado a la cotización`)
    } catch (error) {
      showError('Error al calcular precios')
      console.error(error)
    }
  }

  showSearchModal.value = false
  searchTerm.value = ''
  searchResults.value = []
}

// Recalcular precios cuando cambia la cantidad
const recalcularPreciosItem = async (item) => {
  const negocio = userStore.negocioSeleccionado
  if (!negocio || !item.prototipo_id) return

  try {
    await post('cotizaciones-calcular-precios', {
      prototipo_id: item.prototipo_id,
      cantidad: item.cantidad,
      negocio_id: negocio.id
    })

    const precios = data.value

    // Actualizar todos los precios calculados
    Object.assign(item, {
      ...precios,
      // Mantener la opción seleccionada
      precioVentaFinal: parseFloat(Number(item.precioManualmenteEditado
        ? item.precioVentaFinal
        : precios[`precioVentaOpcion${item.opcionPrecioSeleccionada}`]).toFixed(4)),
      precio_unitario: parseFloat(Number(item.precioManualmenteEditado
        ? item.precioVentaFinal
        : precios[`precioVentaOpcion${item.opcionPrecioSeleccionada}`]).toFixed(4))
    })
  } catch (error) {
    showError('Error al recalcular precios')
    console.error(error)
  }
}

// Cambiar opción de precio seleccionada
const cambiarOpcionPrecio = (item, opcion) => {
  item.opcionPrecioSeleccionada = opcion
  item.precioManualmenteEditado = false
  item.precioVentaFinal = parseFloat(Number(item[`precioVentaOpcion${opcion}`]).toFixed(2))
  item.precio_unitario = parseFloat(Number(item.precioVentaFinal).toFixed(2))
}

// Actualizar precio basado en el tipo seleccionado
// Función para calcular precio dinámico SOLO cuando pago_herramental = 'NO'
const calcularPrecioDinamico = (prototipo, cantidad, tipoPrecio) => {
  const negocio = userStore.negocioSeleccionado
  if (!negocio || !cantidad || cantidad <= 0) return 0

  // Obtener el porcentaje según el tipo de precio
  let porcentaje = 25 // fallback
  switch (tipoPrecio) {
    case 'precio1': porcentaje = parseFloat(negocio.porcentaje1) || 25; break
    case 'precio2': porcentaje = parseFloat(negocio.porcentaje2) || 35; break
    case 'precio3': porcentaje = parseFloat(negocio.porcentaje3) || 50; break
    case 'precio4': porcentaje = parseFloat(negocio.porcentaje4) || 75; break
  }

  const costoBase = parseFloat(prototipo.costo_calculado || 0)
  const valorHerramental = parseFloat(prototipo.valor_pago_herramental || 0)

  // Calcular herramental distribuido según la cantidad específica
  const herramentalPorPieza = valorHerramental / cantidad
  const costoConHerramental = costoBase + herramentalPorPieza
  const precioFinal = costoConHerramental * (1 + porcentaje / 100)
  return parseFloat(precioFinal.toFixed(2))
}

const updatePriceFromSelection = (item) => {
  if (!item.prototipo) return

  const prototipo = item.prototipo

  if (prototipo.pago_herramental === 'SI') {
    // Usar precios guardados en BD (tal como están)
    if (!prototipo.prototipo_negocio_precios || prototipo.prototipo_negocio_precios.length === 0) return

    const preciosNegocio = prototipo.prototipo_negocio_precios[0]

    switch (item.tipo_precio) {
      case 'precio1':
        item.precio_unitario = parseFloat(parseFloat(preciosNegocio.precio1).toFixed(2))
        break
      case 'precio2':
        item.precio_unitario = parseFloat(parseFloat(preciosNegocio.precio2).toFixed(2))
        break
      case 'precio3':
        item.precio_unitario = parseFloat(parseFloat(preciosNegocio.precio3).toFixed(2))
        break
      case 'precio4':
        item.precio_unitario = parseFloat(parseFloat(preciosNegocio.precio4).toFixed(2))
        break
      default:
        item.precio_unitario = parseFloat(parseFloat(preciosNegocio.precio1).toFixed(2))
    }
  } else {
    // Recalcular dinámicamente según cantidad específica
    item.precio_unitario = calcularPrecioDinamico(prototipo, item.cantidad, item.tipo_precio)
  }
}

// Función para manejar cambios en la cantidad
const onCantidadChange = (item) => {
  // Solo recalcular si pago_herramental = 'NO' (porque el herramental se distribuye)
  if (item.prototipo && item.prototipo.pago_herramental === 'NO') {
    updatePriceFromSelection(item)
  }
}

// Remover item
const removeItem = (index) => {
  items.value.splice(index, 1)
}

// Obtener opciones de precios para un prototipo
const getPreciosOptions = (prototipo) => {
  if (!prototipo.prototipo_negocio_precios || prototipo.prototipo_negocio_precios.length === 0) {
    return []
  }

  const preciosNegocio = prototipo.prototipo_negocio_precios[0]
  return [
    { label: `Precio 1 (+${preciosNegocio.porcentaje1}%)`, valor: parseFloat(preciosNegocio.precio1) },
    { label: `Precio 2 (+${preciosNegocio.porcentaje2}%)`, valor: parseFloat(preciosNegocio.precio2) },
    { label: `Precio 3 (+${preciosNegocio.porcentaje3}%)`, valor: parseFloat(preciosNegocio.precio3) },
    { label: `Precio 4 (+${preciosNegocio.porcentaje4}%)`, valor: parseFloat(preciosNegocio.precio4) }
  ]
}

// Auto-guardado
const triggerAutoSave = async () => {

  try {
    const payload = {
      descripcion: cotizacionForm.value.descripcion,
      cliente_id: cotizacionForm.value.cliente_id,
      fecha_vencimiento: cotizacionForm.value.fecha_vencimiento,
      descuento: cotizacionForm.value.descuento,
      notas: cotizacionForm.value.notas,
      items: items.value.map(item => ({
        prototipo_id: item.prototipo_id,
        cantidad: item.cantidad,
        precio_unitario: item.precio_unitario,
        tipo_precio: item.tipo_precio || 'precio1',
        notas: item.notas
      }))
    }

    const endpoint = isEdit.value 
      ? `cotizaciones/${cotizacionId.value}/auto-save`
      : 'cotizaciones/auto-save'

    await post(endpoint, payload)

    // Si se creó una nueva cotización, actualizar la URL y el estado
    if (data.value?.cotizacion_id && !isEdit.value) {
      // Cambiar la URL del navegador para reflejar que ahora estamos editando
      window.history.replaceState({}, '', `/cotizaciones/${data.value.cotizacion_id}/editar`)
    }

    autoSaveStatus.value = 'Guardado automáticamente'
    autoSaveStatusClass.value = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
    lastSavedAt.value = new Date()

    // Limpiar mensaje después de 3 segundos
    setTimeout(() => {
      autoSaveStatus.value = ''
    }, 3000)

  } catch (err) {
    autoSaveStatus.value = 'Error al guardar'
    autoSaveStatusClass.value = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
    
    setTimeout(() => {
      autoSaveStatus.value = ''
    }, 3000)
  }
}

// Configurar auto-guardado
const setupAutoSave = () => {
  // Solo configurar si está habilitado
  if (!autoSaveEnabled.value) return

  // Limpiar timer existente
  if (autoSaveTimer.value) {
    clearInterval(autoSaveTimer.value)
  }

  // Configurar nuevo timer (cada 30 segundos)
  autoSaveTimer.value = setInterval(() => {
    if (cotizacionForm.value.nombre.trim()) {
      triggerAutoSave()
    }
  }, 30000)
}

// Watch para cambios que activen auto-guardado
watch([cotizacionForm, items], () => {
  // Solo auto-guardar si está habilitado
  if (!autoSaveEnabled.value) return

  // Solo auto-guardar si hay nombre y han pasado al menos 5 segundos desde el último guardado
  if (cotizacionForm.value.nombre.trim() &&
      (!lastSavedAt.value || Date.now() - lastSavedAt.value > 5000)) {

    // Debounce: esperar 2 segundos sin cambios antes de guardar
    clearTimeout(autoSaveTimer.value)
    autoSaveTimer.value = setTimeout(triggerAutoSave, 2000)
  }
}, { deep: true })

// Guardar como borrador
const saveDraft = async () => {
  await triggerAutoSave()
}

// Guardar cotización final
const saveCotizacion = async () => {
  try {
    validationErrors.value = {}

    const negocio = userStore.negocioSeleccionado
    if (!negocio) {
      showError('Debes seleccionar un negocio')
      return
    }

    const payload = {
      descripcion: cotizacionForm.value.descripcion,
      cliente_id: cotizacionForm.value.cliente_id,
      fecha_vencimiento: cotizacionForm.value.fecha_vencimiento,
      descuento: cotizacionForm.value.descuento,
      notas: cotizacionForm.value.notas,
      negocio_id: negocio.id,
      estado: 'enviada',
      items: items.value.map(item => ({
        prototipo_id: item.prototipo_id,
        cantidad: item.cantidad,
        opcionPrecioSeleccionada: item.opcionPrecioSeleccionada || 1,
        precioVentaFinal: item.precioVentaFinal || item.precio_unitario,
        precioManualmenteEditado: item.precioManualmenteEditado || false,
        precio_unitario: item.precio_unitario,
        tipo_precio: item.tipo_precio || 'precio1',
        notas: item.notas
      }))
    }

    if (isEdit.value) {
      await put(`cotizaciones/${cotizacionId.value}`, payload)
      showSuccess('Cotización actualizada exitosamente')
    } else {
      await post('cotizaciones', payload)
      showSuccess('Cotización creada exitosamente')
    }

    goBack()
  } catch (err) {
    if (error.value && typeof error.value === 'object' && error.value.errors) {
      validationErrors.value = error.value.errors
    }
    showError(error.value?.message || 'Error al guardar cotización')
  }
}

// Navegación
const goBack = () => {
  router.push('/cotizaciones')
}

// Descargar PDF
const downloadPDF = async () => {
  try {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'
    const token = localStorage.getItem('token')

    const response = await fetch(`${API_BASE_URL}/cotizaciones/${cotizacionId.value}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new Error('Error al descargar el PDF')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `cotizacion-${cotizacionForm.value.numero_cotizacion}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('PDF descargado correctamente')
  } catch (error) {
    console.error('Error al descargar PDF:', error)
    showError('Error al descargar el PDF')
  }
}

// Función para abrir modal de búsqueda de prototipos
const openSearchModal = async () => {
  showSearchModal.value = true
  await loadInitialPrototipos()
}

// Funciones del selector de clientes
const searchClients = async () => {
  try {
    loadingClients.value = true

    // Si hay término de búsqueda, buscar con ese término; si no, traer todos los contactos activos
    const searchParam = clientSearchTerm.value.trim()
      ? `buscar=${encodeURIComponent(clientSearchTerm.value)}&`
      : ''

    await get(`/contactos?${searchParam}`)
    console.log("contactos data: ", data.value);

    // El endpoint puede devolver data.data o solo data
    availableClients.value = data.value.data || data.value || []
  } catch (err) {
    console.error('Error al buscar contactos:', err)
    showError('Error al buscar contactos')
    availableClients.value = []
  } finally {
    loadingClients.value = false
  }
}

// Debounce para búsqueda de clientes
const debouncedSearchClients = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(searchClients, 300)
  }
})()

const openClientSelector = async () => {
  showClientSelector.value = true
  clientSearchTerm.value = ''
  // Cargar clientes inicialmente
  await searchClients()
}

const selectClient = (cliente) => {
  selectedClient.value = cliente

  // Actualizar el formulario con el ID del cliente
  cotizacionForm.value.cliente_id = cliente.id

  // Cerrar el modal
  showClientSelector.value = false
}

const closeClientSelector = () => {
  showClientSelector.value = false
  clientSearchTerm.value = ''
  availableClients.value = []
}

const clearClientSelection = () => {
  selectedClient.value = null
  cotizacionForm.value.cliente_id = null
  showClientSelector.value = false
}

// Funciones para manejo de múltiples borradores
const checkForExistingDrafts = async () => {
  if (isEdit.value) return // No verificar si estamos editando

  try {
    loadingDrafts.value = true
    await get('cotizaciones-my-drafts')
    if (data.value && data.value.length > 0) {
      availableDrafts.value = data.value
      showDraftsModal.value = true
    }
  } catch (error) {
    console.error('Error al cargar borradores:', error)
  } finally {
    loadingDrafts.value = false
  }
}

const continueWithDraft = (draft) => {
  // Redirigir a editar el borrador seleccionado
  router.push(`/cotizaciones/${draft.id}/editar`)
}

const startNewQuotation = () => {
  // Cerrar modal y continuar con nueva cotización
  showDraftsModal.value = false
  availableDrafts.value = []
}

const formatDraftDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffTime = Math.abs(now - date)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 1) return 'Hace 1 día'
  if (diffDays < 7) return `Hace ${diffDays} días`
  if (diffDays < 30) return `Hace ${Math.ceil(diffDays/7)} semanas`
  return date.toLocaleDateString()
}

onMounted(() => {
  console.log('Component mounted, route params:', route.params)
  console.log('isEdit computed value:', isEdit.value)
  console.log('cotizacionId computed value:', cotizacionId.value)

  loadCotizacionData()
  setupAutoSave()

  // Verificar si hay borradores existentes solo para nuevas cotizaciones
  if (!isEdit.value) {
    checkForExistingDrafts()
  }
})

onUnmounted(() => {
  if (autoSaveTimer.value) {
    clearInterval(autoSaveTimer.value)
  }
})
</script>